import { AlertCircle, ExternalLink, Globe, Phone } from 'lucide-react';
import PageHeader from '@/components/layout/PageHeader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface ResourceItem {
  name: string;
  description: string;
  website: string;
  phone?: string;
}

interface ResourceSection {
  category: string;
  items: ResourceItem[];
}

// Checked against the organisations' public sites on 30 August 2026.
// External organisations own their contact details; confirm before relying on them.
const resources: ResourceSection[] = [
  {
    category: 'Legal aid and lawyer referrals',
    items: [
      {
        name: 'Jabatan Bantuan Guaman (JBG)',
        website: 'https://www.jbg.gov.my',
        phone: '03-8885 1827 (civil) · 03-8885 1826 (syariah)',
        description: 'Official Malaysian Legal Aid Department portal and service information.',
      },
      {
        name: 'Yayasan Bantuan Guaman Kebangsaan (YBGK)',
        website: 'https://www.ybgk.org.my',
        phone: '03-8872 3229',
        description: 'Official legal-aid information, including the current criminal-assistance scope and contacts.',
      },
      {
        name: 'The Malaysian Bar',
        website: 'https://www.malaysianbar.org.my',
        phone: '03-2050 2050',
        description: 'Bar Council information and lawyer-referral resources.',
      },
    ],
  },
  {
    category: 'Emergency and welfare contacts',
    items: [
      {
        name: 'Police emergency',
        website: 'https://rmp2u.rmp.gov.my/faq',
        phone: '999',
        description: 'For an emergency requiring immediate police assistance.',
      },
      {
        name: "Women's Aid Organisation (WAO)",
        website: 'https://wao.org.my/contact-us/',
        phone: '03-3000 8858',
        description: 'Domestic-violence support and referral information.',
      },
      {
        name: 'Talian Kasih',
        website: 'https://www.kpwkm.gov.my/portal-main/article?id=talian-kasih',
        phone: '15999 · WhatsApp 019 26 15999',
        description: '24-hour welfare and social-support channel operated by KPWKM.',
      },
    ],
  },
  {
    category: 'Courts, tribunals and employment',
    items: [
      {
        name: 'Malaysian Judiciary',
        website: 'https://www.kehakiman.gov.my',
        description: 'Official court and judiciary information.',
      },
      {
        name: 'Tribunal for Consumer Claims Malaysia',
        website: 'https://ttpm.kpdn.gov.my/?lang=en',
        phone: '03-8882 5822 · toll-free 1-800-88-9811',
        description: 'Official e-Tribunal portal and consumer-claims information.',
      },
      {
        name: 'Jabatan Tenaga Kerja Semenanjung Malaysia (JTKSM)',
        website: 'https://jtksm.mohr.gov.my/en/home',
        phone: '03-8000 8000',
        description: 'Official labour-department portal for employment services and complaints.',
      },
    ],
  },
  {
    category: 'Government services',
    items: [
      {
        name: 'MyEG',
        website: 'https://www.myeg.com.my',
        description: 'Online government-service access point.',
      },
      {
        name: 'National Registration Department (JPN)',
        website: 'https://www.jpn.gov.my',
        description: 'Identity and civil-registration services.',
      },
      {
        name: 'Companies Commission of Malaysia (SSM)',
        website: 'https://www.ssm.com.my',
        description: 'Business registration and company information services.',
      },
    ],
  },
];

function phoneHref(phone: string) {
  const firstNumber = phone.match(/[+\d][\d\s-]*/)?.[0] ?? phone;
  return `tel:${firstNumber.replace(/[^+\d]/g, '')}`;
}

export default function LegalResources() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <main className="mx-auto w-full max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center justify-between gap-4">
          <a href="/" className="font-playfair text-2xl font-bold tracking-tight text-foreground hover:text-primary">
            MyLegal<span className="text-primary">AI</span>
          </a>
          <a href="/archive" className="text-sm font-medium text-primary underline underline-offset-4">Archive notice</a>
        </div>
        <PageHeader
          eyebrow="Malaysia · public information"
          title="Legal resources"
          description="Official starting points for legal aid, emergency assistance, courts, tribunals and government services."
        />

        <Alert className="mb-10 border-gold/30 bg-gold/5">
          <AlertCircle className="h-5 w-5 text-gold" />
          <AlertTitle>Need urgent help?</AlertTitle>
          <AlertDescription>
            Call <strong>999</strong> for police emergency assistance, <strong>15999</strong> for Talian Kasih, or WAO at <strong>03-3000 8858</strong> for domestic-violence support. Confirm current details on the linked official site.
          </AlertDescription>
        </Alert>

        <div className="space-y-10">
          {resources.map((section) => (
            <section key={section.category} className="space-y-4">
              <h2 className="text-lg font-semibold text-foreground">{section.category}</h2>
              <div className="grid gap-4 md:grid-cols-2">
                {section.items.map((item) => (
                  <Card key={item.name} className="border-border/80 bg-card/80">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">{item.name}</CardTitle>
                      <CardDescription>{item.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      {item.phone && (
                        <div className="flex items-start gap-2">
                          <Phone className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden />
                          <a href={phoneHref(item.phone)} className="text-primary hover:underline">{item.phone}</a>
                        </div>
                      )}
                      <div className="flex items-start gap-2">
                        <Globe className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden />
                        <a href={item.website} target="_blank" rel="noopener noreferrer" className="break-all text-primary hover:underline">
                          {item.website.replace(/^https:\/\//, '')}
                          <ExternalLink className="ml-1 inline h-3.5 w-3.5" aria-hidden />
                        </a>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          ))}
        </div>

        <p className="mt-10 border-t border-border pt-6 text-sm leading-relaxed text-muted-foreground">
          This directory is informational and may become outdated. MyLegalAI does not provide legal advice, endorse any organisation, or handle requests submitted through external websites.
        </p>
      </main>
    </div>
  );
}
